import classNames from "classnames";
import moment from "moment-timezone";
import { useMemo } from "react";
import { InputGroup, Tooltip } from "reactstrap";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock } from "@fortawesome/free-solid-svg-icons";
import CustomTimeInput from "../../../../../components/Inputs/CustomTimeInput";
import { applyTimezone, combineDateAndTime } from "../../../../../utils/helpers/date";
import { getTimeSheetTimeZone, isAddedTimeSheet, isTimeInputDisabled } from "../helpers";
import { useCompanyTimeFormat } from "../../../../../utils/hooks/company";
import { useAddedPayCode } from "../hooks/useAddedPayCode";
import { getClockInputStyle, ExceptionTooltip, canTimeSheetBeBinded } from "./useInColumn";
import { useVisible } from "../../../../../utils/hooks/useVisible";

const MissPunchTooltip = ({ original, openMissPunchModal, isMyTimesheet }) => {
    const { t } = useTranslation();
    const { visible, toggle } = useVisible();
    const id = `miss-punch-${original.user.id}-row-${original.id}`;

    let message;
    switch (original.missPunchStatus) {
        case "pending":
            message = t("miss-punch-request-info")
            break;
        case "approved":
            message = t("miss-punch-request-approved")
            break;
        default: {
            if (isMyTimesheet) {
                message = t("click-to-request-miss-punch-adjustment")
            } else {
                message = t("is-miss-punch")
            }
        }
    }

    return (
        <>
            <span
                id={id}
                className={
                    classNames(
                        "ml-2 font-weight-bolder text-lg",
                        isMyTimesheet && "cursor-pointer",
                        "text-orange",
                    )
                }
                onClick={
                    (isMyTimesheet && !original.missPunchStatus)
                        ? () => openMissPunchModal(original)
                        : undefined
                }
            >
                &#9888;
            </span>

            <Tooltip target={id} isOpen={visible} toggle={toggle}>
                {message}
            </Tooltip>
        </>
    );
}

export const useOutColumn = ({
    user,
    canEdit,
    clockExceptions,
    view,
    openInOutDateModal,
    openMissPunchModal,
    openSyncTimesheetModal,
    isMyTimesheet
}) => {
    const { t } = useTranslation();
    const timeFormat = useCompanyTimeFormat();
    const payCode = useAddedPayCode(user);
    const getInputStyle = useMemo(() => {
        if (timeFormat) {
            switch (timeFormat.length) {
                case 1:
                case 2:
                case 3:
                case 4:
                case 5:
                    return { width: '60px' };
                case 6:
                case 7:
                    return { width: '70px' };
                case 8:
                case 9:
                case 10:
                    return { width: '80px' };
                default:
                    return { width: '116px' }
            }
        }
        return { width: '116px' }
    }, [timeFormat])

    return useMemo(() => ({
        Header: t("out"),
        accessor: "endDate",
        alwaysVisible: true,
        headCustomProps: { style: getInputStyle },
        Cell: ({ value, row, cell, updateAddedTimeSheet, updateTimeSheet, data }) => {
            const { original, isNonProductive } = row;
            const disabled = isTimeInputDisabled(original) || row.disabled || cell.disabled || isNonProductive;
            const timeZone = getTimeSheetTimeZone(original);
            const currentValue = value
                ? timeZone
                    ? moment.parseZone(value).toISOString(true)
                    : value
                : undefined;

            const style = getClockInputStyle({
                color: "#ff6161",
                backgroundColor: "#ff61611a",
                border: `1px solid transparent`,
                value,
                original,
            });

            if (original?.misspunch) {
                style.borderWidth = "2px";
            }

            let exceptions = clockExceptions?.filter((exception) => moment(original?.date, "YYYY-MM-DD").isSame(moment(exception?.date, "YYYY-MM-DD"), "date"));
            exceptions = exceptions?.map((exception) => exception?.data);
            exceptions = (exceptions || []).flat();
            exceptions = exceptions?.filter((exception) =>
            (
                [
                    "left_early",
                    "left_late"
                ].includes(exception?.type) &&
                exception?.timesheet?.id === original?.id
            )
            );

            if (openInOutDateModal && original?.endDate) {
                style.borderLeft = 0;
            }

            return (
                <div className="d-flex align-items-center">
                    {isNonProductive || ["unit", "amount"].includes(original?.payCode?.hourType) ? "-" :
                        <InputGroup className="custom-time-input-group">
                            {(openInOutDateModal && original?.endDate && canEdit) && (
                                <span
                                    className={classNames("clock-icon text-center px-1", value && "filled",
                                        original?.payCode?.hourType === "unpaid" && "font-weight-400")}
                                    style={{
                                        ...style,
                                        padding: "0.1rem 0.4rem",
                                    }}
                                >
                                    <FontAwesomeIcon
                                        className="cursor-pointer"
                                        icon={faClock}
                                        onClick={() => openInOutDateModal({
                                            column: "out",
                                            user,
                                            original,
                                            updateAddedTimeSheet,
                                            updateTimeSheet
                                        })}
                                        style={{ fontSize: "1rem", paddingTop: "0.2rem" }}
                                    />
                                </span>
                            )}
                            <CustomTimeInput
                                timeFormat={timeFormat}
                                className={classNames("time-input text-center", value && "filled", (value && canEdit) && "input-with-value",
                                    original?.payCode?.hourType === "unpaid" && "font-weight-400")}
                                style={style}
                                disabled={disabled || !canEdit || original?.readOnly}
                                placeholder={["unit", "amount"].includes(original?.payCode?.hourType) ? "-- : --" : t("time")}
                                value={currentValue}
                                onChange={(val) => {
                                    let endDate = "";
                                    if (val) {
                                        endDate = combineDateAndTime(
                                            moment(original.date, "YYYY-MM-DD"),
                                            moment(val),
                                        );
                                        endDate = applyTimezone(endDate, timeZone, true);
                                        if (original.startDate && endDate.isBefore(original.startDate)) {
                                            endDate.add(1, "day");
                                        }

                                        // update sibling
                                        // ?? maybe on binded field change, open a modal to confirm the binding change
                                        // ?? should bind continue if the change causes a cut / conflict / other issues
                                        // TODO: bind should stop if user unbinded the timesheets
                                        if (original.endDate && canTimeSheetBeBinded(original) && openSyncTimesheetModal) {
                                            const sibling = data.find((item) => {
                                                if (!item.startDate || !canTimeSheetBeBinded(item)) {
                                                    return false;
                                                }
                                                if (item.id === original.id) {
                                                    return false;
                                                }
                                                return moment.parseZone(item.startDate)
                                                    .isSame(moment.parseZone(original.endDate), "minute");
                                            });
                                            // TODO: detect swallous / conflicting timesheets / other issues due to change / binding
                                            if (sibling) {
                                                const startDate = endDate.toISOString(true);
                                                // end date can not be used inside the callback, because its value changes asynchronously
                                                const onSuccess = () => {
                                                    const callback = isAddedTimeSheet(sibling)
                                                        ? updateAddedTimeSheet.bind(null, sibling)
                                                        : updateTimeSheet.bind(null, sibling.id)
                                                    callback({ startDate });
                                                }
                                                openSyncTimesheetModal({
                                                    field: t("start-date").toLowerCase(),
                                                    onSuccess,
                                                })
                                            }
                                        }
                                        endDate = endDate.toISOString(true);
                                    }

                                    const duration = original?.startDate && endDate
                                        ? moment(endDate).diff(moment(original?.startDate), "seconds")
                                        : original?.duration || 0;

                                    const updatedFields = { endDate, duration };
                                    if (val && val !== "") {
                                        if (isAddedTimeSheet(original)) {
                                            if (!original?.payCode) {
                                                updatedFields.payCode = payCode;
                                            }
                                            updateAddedTimeSheet(original, updatedFields);
                                        } else {
                                            updateTimeSheet(original.id, updatedFields);
                                        }
                                    }
                                }}
                            />
                        </InputGroup>
                    }

                    {(exceptions?.length > 0 && view) ? (
                        <ExceptionTooltip
                            original={original}
                            view={view}
                            exceptions={exceptions}
                            style={{
                                color: "#ff6161"
                            }}
                        />
                    ) : null}

                    {(original.misspunch && openMissPunchModal) && (
                        <MissPunchTooltip
                            openMissPunchModal={openMissPunchModal}
                            original={original}
                            isMyTimesheet={isMyTimesheet}
                        />
                    )}
                </div >
            );
        },
    }), [
        t,
        getInputStyle,
        clockExceptions,
        openInOutDateModal,
        timeFormat,
        canEdit,
        view,
        user,
        payCode,
        isMyTimesheet,
        openSyncTimesheetModal,
        openMissPunchModal,
    ]);
};
